import { useCallback } from "react"
import type { PublicMessage } from "appsShared/inAppMessages"
import { useAppDispatch } from "../../app/hooks"
import { storeDismissal } from "./inAppLocalState"
import { inAppMessagesActions } from "./inAppMessagesSlice"

/**
 * Close handlers per the message's `dismiss` policy (spec §Consumer local state).
 * `dismissHandler` is undefined for `dismiss: none`, so the slot renders no close control.
 * `closeHandler` always closes (a modal must be closable): it stores the dismissal when the
 * policy allows one, and otherwise hides the message for this page session only.
 */
export function useInAppMessageClose() {
  const dispatch = useAppDispatch()

  const dismiss = useCallback(
    (message: PublicMessage) => {
      const at = new Date().toISOString()
      if (storeDismissal(message, at))
        dispatch(inAppMessagesActions.messageDismissed({ id: message.id, at }))
      else dispatch(inAppMessagesActions.messageClosed(message.id))
    },
    [dispatch],
  )

  const dismissHandler = useCallback(
    (message: PublicMessage) =>
      message.dismiss === "none" ? undefined : () => dismiss(message),
    [dismiss],
  )

  const closeHandler = useCallback(
    (message: PublicMessage) => () => dismiss(message),
    [dismiss],
  )

  return { dismissHandler, closeHandler }
}
